import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { authService } from '@shared/services/auth.service'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Alert, AlertDescription } from '@shared/components/ui/alert'
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'

// Validation schema
const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1, 'Huidig wachtwoord is verplicht'),
  newPassword: z.string()
    .min(8, 'Wachtwoord moet minimaal 8 tekens bevatten')
    .regex(/[A-Z]/, 'Moet minimaal één hoofdletter bevatten')
    .regex(/[a-z]/, 'Moet minimaal één kleine letter bevatten')
    .regex(/[0-9]/, 'Moet minimaal één cijfer bevatten'),
  confirmPassword: z.string()
}).refine(data => data.newPassword === data.confirmPassword, {
  message: 'Wachtwoorden komen niet overeen',
  path: ['confirmPassword']
}).refine(data => data.currentPassword !== data.newPassword, {
  message: 'Nieuw wachtwoord moet verschillen van huidig wachtwoord',
  path: ['newPassword']
})

type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>

export function PasswordChangeForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
  })

  const newPassword = watch('newPassword')

  // Password strength indicator
  const getPasswordStrength = (password: string | undefined) => {
    if (!password) return { strength: 0, label: '', color: '' }

    let strength = 0
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    const labels = ['Zeer zwak', 'Zwak', 'Matig', 'Sterk', 'Zeer sterk']
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-emerald-500']

    return {
      strength: Math.min(strength, 5),
      label: labels[Math.min(strength - 1, 4)] || '',
      color: colors[Math.min(strength - 1, 4)] || ''
    }
  }

  const passwordStrength = getPasswordStrength(newPassword)

  const onSubmit = async (data: PasswordChangeFormValues) => {
    setIsSubmitting(true)
    setSuccessMessage('')
    setErrorMessage('')

    try {
      const result = await authService.updatePassword(data.currentPassword, data.newPassword)

      if (result.success) {
        setSuccessMessage('Uw wachtwoord is succesvol gewijzigd!')
        reset()

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('')
        }, 5000)
      } else {
        setErrorMessage(result.error || 'Er is een fout opgetreden bij het wijzigen van uw wachtwoord')
      }
    } catch (error) {
      console.error('Password change error:', error)
      setErrorMessage('Er is een onverwachte fout opgetreden. Probeer het later opnieuw.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Success Alert */}
      {successMessage && (
        <Alert className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {errorMessage && (
        <Alert className="bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertDescription className="text-red-800 dark:text-red-200">
            {errorMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Current Password Field */}
      <div className="space-y-2">
        <Label htmlFor="currentPassword">Huidig wachtwoord</Label>
        <div className="relative">
          <Input
            {...register('currentPassword')}
            id="currentPassword"
            type={showCurrentPassword ? 'text' : 'password'}
            className={errors.currentPassword ? 'border-red-500' : ''}
            disabled={isSubmitting}
            autoComplete="current-password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            tabIndex={-1}
          >
            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="text-sm text-red-600 dark:text-red-400">{errors.currentPassword.message}</p>
        )}
      </div>

      {/* New Password Field */}
      <div className="space-y-2">
        <Label htmlFor="newPassword">Nieuw wachtwoord</Label>
        <div className="relative">
          <Input
            {...register('newPassword')}
            id="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            className={errors.newPassword ? 'border-red-500' : ''}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowNewPassword(!showNewPassword)}
            tabIndex={-1}
          >
            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.newPassword && (
          <p className="text-sm text-red-600 dark:text-red-400">{errors.newPassword.message}</p>
        )}

        {/* Password Strength Indicator */}
        {newPassword && (
          <div className="space-y-1">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`h-1 flex-1 rounded-full ${
                    level <= passwordStrength.strength ? passwordStrength.color : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>
            {passwordStrength.label && (
              <p className="text-xs text-muted-foreground">
                Wachtwoordsterkte: {passwordStrength.label}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Confirm Password Field */}
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Bevestig nieuw wachtwoord</Label>
        <div className="relative">
          <Input
            {...register('confirmPassword')}
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            className={errors.confirmPassword ? 'border-red-500' : ''}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            tabIndex={-1}
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-sm text-red-600 dark:text-red-400">{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full sm:w-auto"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wachtwoord wijzigen...
          </>
        ) : (
          'Wachtwoord wijzigen'
        )}
      </Button>

      {/* Password Requirements Info */}
      <div className="mt-4 p-3 bg-muted/50 rounded-md">
        <p className="text-sm font-medium mb-2">Wachtwoordvereisten:</p>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Minimaal 8 tekens</li>
          <li>• Minimaal één hoofdletter (A-Z)</li>
          <li>• Minimaal één kleine letter (a-z)</li>
          <li>• Minimaal één cijfer (0-9)</li>
          <li>• Moet verschillen van huidig wachtwoord</li>
        </ul>
      </div>
    </form>
  )
}